// @ts-nocheck: converted from JS

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import StepForm from '@/components/UI/StepForm';
import SubHeader from '@/components/UI/SubHeader';
import { successToast } from '@/helpers';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { createMeta } from '@/support/hot';
import { createMetaV16 } from '@/support/hot-v16';
import Http from '@/support/http';
import { api as apiRoutes, web as webRoute } from '@/support/route';
import { lazy, Suspense, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { parseQueryString } from '@/helpers';
import { featuresSelector } from '@/support/Selectors';
import { AnimalSexLabelToCode } from '@/model/Animal.utils';
import type { AnimalSexLabel } from '@/model/Animal.model';
import { initAnimalSettings, initCageSettings } from '../Colony.utils';
import { initCageSettingsV16 } from './Cages.utils.v16';
import Animals from './Animals';
import Summary from './Summary';

const Cages = lazy(() => import('./Cages'));
const CagesV16 = lazy(() => import('./CagesV16'));
import './../Colony.scss';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
  unknownSexEnabled: features?.unknown_sex === true,
}));

const ColonyCreation = () => {
  const location = useLocation();
  const { study: studyId } = parseQueryString(location.search);
  const navigate = useNavigate();
  const { hotUpgrade, unknownSexEnabled } = useSelector(hotUpgradeSelector);

  const CagesComponent = hotUpgrade ? CagesV16 : Cages;
  const ColonyCreationCages = _notNil(studyId) ? withStudy(CagesComponent, studyId) : CagesComponent;
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');

  const returnText = _notNil(studyId) ? 'Cages' : 'Colony';
  const returnLink = _notNil(studyId) ? webRoute('studies.cages', { id: studyId }) : webRoute('colony');

  const steps = [
    {
      name: 'Cages',
      isValid: ({ cages: { cellErrors } }) => {
        const cellErrorValues = Object.values(cellErrors);
        if (_isNotEmpty(cellErrorValues) || loading) {
          return cellErrorValues[cellErrorValues.length - 1];
        }
        return true;
      },
      renderStep: (state, dispatch) => (
        <Suspense fallback={<Loading />}>
          <ColonyCreationCages state={state} dispatch={dispatch} />
        </Suspense>
      ),
    },
    {
      name: 'Add animals',
      isValid: ({ animals: { cellErrors } }) => {
        if (_isNotEmpty(Object.values(cellErrors))) {
          return Object.values(cellErrors).join('</br>');
        }
        return true;
      },
      renderStep: (state, dispatch) => (
        <Animals state={state} dispatch={dispatch} entityFieldRequiredMap={entityFieldRequiredMap} />
      ),
    },
    {
      name: 'Summary',
      isValid: (state) => true,
      renderStep: (state, dispatch) => <Summary state={state} dispatch={dispatch} />,
    },
  ];
  const initialState = {
    steps,
    currentStep: 0,
    complete: false,
    stepReady: false,
    cages: {
      initialData: [],
      data: [],
      settings: initCageSettings(false, true).settings,
      settingsV16: initCageSettingsV16(),
      metadata: [],
      metaGlossaries: [],
      cellErrors: { 'no-cage': 'You must supply at least 1 cage' },
      overlay: '',
      invalidCells: {},
      cageCount: 1,
      maxRows: 0,
    },
    animals: {
      species: undefined,
      initialData: [],
      data: [],
      settings: initAnimalSettings(
        [],
        true,
        false,
        undefined,
        undefined,
        undefined,
        entityFieldRequiredMap,
        unknownSexEnabled
      ).settings,
      cellErrors: {},
      metadata: [],
      metaGlossaries: [],
      invalidCells: {},
      overlay: '',
    },
    Summary: {},
    apiError: undefined,
  };
  const [loading, setLoading] = useState(false);

  const formatCurrentCageAnimals = (cageCatalog, state) => {
    const {
      animals: { settings: animalSettings, v16Settings: animalV16Settings, data: animalData, species },
    } = state;
    const animalColumns = hotUpgrade ? animalV16Settings?.columns : animalSettings?.columns;
    const metaFormatter = hotUpgrade ? createMetaV16 : createMeta;
    return animalData.reduce((acc, { cage_catalog: catalog, name, sex, species: animalSpecies, dob, strain, meta }) => {
      if (cageCatalog === catalog) {
        const returnData = {
          name,
        };
        if (sex) {
          returnData.sex = AnimalSexLabelToCode[sex as AnimalSexLabel];
        }
        if (dob) {
          returnData.dob = dob;
        }
        if (animalSpecies) {
          const strains = species.find((species) => species.name === animalSpecies).strains;
          if (strains.length) {
            returnData.strain_id = strains.find((s) => s.name === strain).id;
          }
        }
        if (meta && _isNotEmpty(meta)) {
          returnData.meta = metaFormatter(meta, animalColumns);
        }
        acc.push(returnData);
      }
      return acc;
    }, []);
  };

  const updateApiError = (dispatch, apiError) => {
    dispatch({
      type: 'update',
      id: 'animals',
      data: apiError,
    });
  };

  const formatCageData = (state) => {
    const {
      cages: { data: cageData, settings: cageSettings, settingsV16 },
    } = state;
    const columns = hotUpgrade ? settingsV16?.columns : cageSettings?.columns;
    const cageMetaFormatter = hotUpgrade ? createMetaV16 : createMeta;
    return cageData
      .filter((cage) => cage.name)
      .map(({ catalog, name, population, meta }) => ({
        name,
        animal_count: population,
        study_id: studyId ? Number(studyId) : null,
        meta: _isEmpty(meta) ? [] : cageMetaFormatter(meta, columns),
        animals: formatCurrentCageAnimals(catalog, state),
      }));
  };

  const saveColony = (state, dispatch) => {
    setLoading(true);
    const formattedData = formatCageData(state);
    Http.post(apiRoutes('cages.createMany'), {
      cages: formattedData,
    })
      .then(({ data }) => {
        navigate('/colony');
        successToast(`Successfully added ${data.length > 1 ? 'colonies' : 'colony'}`);
        if (studyId) {
          navigate(
            webRoute('studies.cages', {
              id: studyId,
            })
          );
        }
      })
      .catch(() => {
        updateApiError(dispatch, 'Failed to create Colony or Animals');
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const errorBanner = ({ state: { apiError } }) => (
    <>
      {apiError && (
        <ApiErrorBanner
          className="mb4"
          title={apiError}
          text={
            'An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support.'
          }
          error={apiError}
        />
      )}
    </>
  );
  return (
    <div className="h-100">
      <SubHeader linkToText={returnText} link={returnLink} />

      <div className="pa4">
        {loading ? (
          <div style={{ height: 650 }}>
            <Loading txt="Updating colony..." />
          </div>
        ) : (
          <StepForm
            title="Add new cages and animals"
            titlePadding="pb2"
            initialState={initialState}
            props={{}}
            steps={steps}
            finalStepAction={saveColony}
            finalButtonText="Create"
            finalButtonClass="w-47 mw6 center"
            horizontal
            errorBanner={errorBanner}
          />
        )}
      </div>
    </div>
  );
};

const withStudy = (CageComponent, studyId) => (props) => {
  const { entity: study } = useFetchEntity({
    entityType: 'study',
    params: { id: studyId },
    includes: 'counts',
  });

  return <CageComponent study={study} {...props} />;
};

export default ColonyCreation;
